import React, { useMemo, useState } from "react";
import Card from "react-bootstrap/Card";
import "./HomeCard.css";
import scriptureData from "../../../data/home/prayers.json";
import useVerseUrl from "../../../utils/useVerseUrl";

const SCRIPTURE_ENTRIES = Array.isArray(scriptureData)
  ? scriptureData
  : [];

function getDayOfYear(date) {
  const startOfYear = Date.UTC(date.getFullYear(), 0, 1);
  const currentDate = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const oneDay = 1000 * 60 * 60 * 24;

  return Math.floor((currentDate - startOfYear) / oneDay) + 1;
}

function formatLabel(value, fallback = "") {
  if (!value) return fallback;

  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getCardLabel(offset) {
  if (offset === -1) return "Yesterday's Scripture";
  if (offset === 1) return "Tomorrow's Scripture";

  return "Today's Scripture";
}

function getReference(entry) {
  const book = entry.book || entry.title || "";

  return (
    entry.reference ||
    `${book} ${entry.chapter}:${entry.verse}`
  );
}

function getChapterReference(entry) {
  const book = entry.book || entry.title || "";

  return (
    entry.chapterReference ||
    `${book} ${entry.chapter}`
  );
}

function HomeCard() {
  const verseUrl = useVerseUrl();
  const [offset, setOffset] = useState(0);

  const entryCount = SCRIPTURE_ENTRIES.length;
  const currentDayOfYear = getDayOfYear(new Date());

  const scriptureEntry = useMemo(() => {
    if (!entryCount) return null;

    const currentIndex =
      (currentDayOfYear - 1) % entryCount;

    const selectedIndex =
      (currentIndex + offset + entryCount) %
      entryCount;

    return SCRIPTURE_ENTRIES[selectedIndex];
  }, [currentDayOfYear, entryCount, offset]);

  if (!scriptureEntry) {
    return (
      <div className="homePrayerCardWrap">
        <Card className="homePrayerCard">
          <Card.Body className="homePrayerCardBody">
            <Card.Title as="h2" className="homePrayerTitle">
              Today's Scripture
            </Card.Title>

            <Card.Text className="homePrayerEmpty">
              No Scripture entry is available right now.
            </Card.Text>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const category =
    scriptureEntry.category || "scripture";

  const categoryClass =
    `homePrayerCard--${category}`;

  const reference = getReference(scriptureEntry);

  const chapterReference =
    getChapterReference(scriptureEntry);

  const scriptureFocus =
    scriptureEntry.scriptureFocus ||
    scriptureEntry.description ||
    "";

  const background =
    scriptureEntry.background ||
    `Read ${chapterReference} to explore the surrounding context of this passage.`;

  return (
    <div className="homePrayerCardWrap">
      <Card className={`homePrayerCard ${categoryClass}`}>
        <Card.Body className="homePrayerCardBody">
          <div className="homePrayerHeader">
            <Card.Title as="h2" className="homePrayerTitle">
              {getCardLabel(offset)}
            </Card.Title>

            <span className="homePrayerThemeBadge">
              {formatLabel(
                scriptureEntry.theme,
                "Scripture"
              )}
            </span>
          </div>

          <section className="homePrayerVerse">
            <div className="homePrayerFieldLabel">
              Scripture Focus
            </div>

            <p className="homePrayerVerseText">
              {scriptureFocus}
            </p>

            <a
              className="homePrayerReferenceLink"
              href={verseUrl(reference)}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${reference} in your selected Bible version`}
            >
              {reference}
            </a>
          </section>

          <section className="homePrayerBackground">
            <div className="homePrayerFieldLabel">
              Background
            </div>

            <p>{background}</p>
          </section>

          <a
            className="homePrayerOpenPassage"
            href={verseUrl(chapterReference)}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open the full chapter of ${chapterReference} in your selected Bible version`}
            data-verse-reference={chapterReference}
          >
            <span>
              <small>Open Passage</small>
              Read {chapterReference}
            </span>

            <span aria-hidden="true">→</span>
          </a>

          <div
            className="homePrayerNav"
            aria-label="Daily Scripture navigation"
          >
            <button
              type="button"
              className="--btn --btn-secondary homePrayerNavBtn"
              onClick={() => setOffset(-1)}
              disabled={offset === -1}
            >
              Yesterday
            </button>

            <button
              type="button"
              className="--btn --btn-secondary homePrayerNavBtn"
              onClick={() => setOffset(0)}
              disabled={offset === 0}
            >
              Today
            </button>

            <button
              type="button"
              className="--btn --btn-secondary homePrayerNavBtn"
              onClick={() => setOffset(1)}
              disabled={offset === 1}
            >
              Tomorrow
            </button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default HomeCard;